<script setup lang="ts">
import { computed } from 'vue';
import type { DashboardResource } from '@/types/planetDashboard';

/**
 * Tooltip riche pour une ressource affichee dans la barre header. Utilise le
 * langage visuel unifié `.tt-*` (cf assets/styles/_tooltips.scss) ; rendu dans
 * une <AppInfoCard>. Affiche :
 *   - dispo actuel
 *   - production horaire (vert si positif, rouge si negatif)
 *   - capacite de stockage + barre de progression (0 = illimite)
 */
const props = defineProps<{
  resource: DashboardResource;
}>();

// Le tooltip (vue detaillee au survol) affiche les valeurs EXACTES avec
// separateurs de milliers, contrairement aux chips de la barre qui abregent
// (k/M). Quantites = entier exact ; taux horaire = jusqu'a 2 decimales.
function formatNumber(value: number): string {
  return Math.round(value).toLocaleString('fr-FR');
}
function formatRate(value: number): string {
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

const fillRatio = computed(() => {
  const cap = props.resource.stored;
  if (cap <= 0) return 0;
  const r = props.resource.available / cap;
  return Math.max(0, Math.min(1, r));
});

const fillPercent = computed(() => Math.round(fillRatio.value * 100));

const isUnlimited = computed(() => props.resource.stored <= 0);
const isSaturated = computed(() => !isUnlimited.value && fillRatio.value >= 0.98);
const productionPositive = computed(() => props.resource.productionPerHour >= 0);

// Le tooltip s'adapte au COMPORTEMENT (EResourceCategory) :
//   - Consommable : stock + capacité + production (classique).
//   - Flux (énergie) : non stocké -> solde net instantané.
//   - Population (habitants) : population courante / capacité de logement + croissance.
//   - Devise (flouz) : solde global + revenu.
const category = computed(() => props.resource.category ?? 'Consommable');
const isFlux = computed(() => category.value === 'Flux');
const isPopulation = computed(() => category.value === 'Population');
const isDevise = computed(() => category.value === 'Devise');

// TENDANCE démographique (≠ HUMEUR/satisfaction, qui dépend du bien-être + taxes
// + famine et s'affiche sur la fiche planète). Ici, simple lecture de la
// croissance : positive = prospère, nulle = stable, négative = déclin/famine.
// Étiquetée « Tendance » pour ne pas contredire l'humeur de la fiche planète.
const popTrend = computed(() => {
  const g = props.resource.productionPerHour;
  if (g > 0) return { emoji: '🙂', label: 'En croissance' };
  if (g < 0) return { emoji: '😟', label: 'En déclin (famine ?)' };
  return { emoji: '😐', label: 'Stable' };
});

// Blessés de combat en convalescence (soignés par l'Hôpital au tick).
// Affiché seulement s'il y en a — sinon on n'encombre pas le tooltip.
const woundedPopulation = computed(() => props.resource.woundedPopulation ?? 0);
const hasWounded = computed(() => woundedPopulation.value > 0);

// Main-d'œuvre : requise (Σ bâtiments) vs disponible (population − équipage).
// laborRatioPct < 100 ⇒ production globale au prorata (pénurie de bras).
const laborUsed = computed(() => props.resource.laborUsed ?? 0);
const laborAvailable = computed(() => props.resource.laborAvailable ?? 0);
const laborRatioPct = computed(() => props.resource.laborRatioPct ?? 100);
// Données labor disponibles dès qu'un bâtiment consomme de la main-d'œuvre.
const hasLabor = computed(() => props.resource.laborUsed != null);
const laborShort = computed(() => laborRatioPct.value < 100);
</script>

<template>
  <div class="tt-card">
    <div class="tt-head">
      <img
        :src="`/images/thumb/64/resources/${resource.code}.png`"
        :alt="resource.label"
        class="tt-icon"
        loading="lazy"
        v-img-hide-on-error
      />
      <span class="tt-name">{{ resource.label }}</span>
      <span class="tt-code">{{ resource.code }}</span>
    </div>

    <!-- Flux (énergie) : non stocké -> solde net instantané (prod - conso). -->
    <template v-if="isFlux">
      <ul class="tt-rows">
        <li class="tt-row">
          <span class="tt-label">Solde net</span>
          <span class="tt-value" :class="productionPositive ? 'is-pos' : 'is-neg'">
            {{ productionPositive ? '+' : '' }}{{ formatRate(resource.productionPerHour) }}/h
          </span>
        </li>
      </ul>
      <p class="tt-note">
        Flux instantané — non stocké. Excédent perdu ; déficit ⇒ délestage de bâtiments.
      </p>
    </template>

    <!-- Population (habitants) : courante / capacité de logement + croissance. -->
    <template v-else-if="isPopulation">
      <ul class="tt-rows">
        <li class="tt-row">
          <span class="tt-label">Population</span>
          <span class="tt-value">{{ formatNumber(resource.available) }}</span>
        </li>
        <li class="tt-row">
          <span class="tt-label">Logement</span>
          <span v-if="isUnlimited" class="tt-value text-muted">illimité</span>
          <span v-else class="tt-value" :class="{ 'is-sat': isSaturated }">
            {{ formatNumber(resource.available) }} / {{ formatNumber(resource.stored) }}
            <span class="tt-pct">({{ fillPercent }}%)</span>
          </span>
        </li>
        <li class="tt-row">
          <span class="tt-label">Croissance</span>
          <span class="tt-value" :class="productionPositive ? 'is-pos' : 'is-neg'">
            {{ productionPositive ? '+' : '' }}{{ formatRate(resource.productionPerHour) }}/h
          </span>
        </li>
        <li class="tt-row">
          <span class="tt-label">Tendance</span>
          <span class="tt-value">{{ popTrend.emoji }} {{ popTrend.label }}</span>
        </li>
        <li v-if="hasWounded" class="tt-row">
          <span class="tt-label">🩹 Blessés</span>
          <span class="tt-value is-neg">{{ formatNumber(woundedPopulation) }}</span>
        </li>
        <li v-if="hasLabor" class="tt-row">
          <span class="tt-label">Main-d'œuvre</span>
          <span class="tt-value" :class="{ 'is-neg': laborShort }">
            {{ formatNumber(laborUsed) }} / {{ formatNumber(laborAvailable) }}
            <span class="tt-pct">({{ laborRatioPct }}%)</span>
          </span>
        </li>
      </ul>
      <div v-if="!isUnlimited" class="tt-bar" :class="{ 'is-sat': isSaturated }">
        <div class="tt-bar-fill" :style="{ width: `${fillPercent}%` }" />
      </div>
      <p v-if="isSaturated" class="tt-warn">Logement plein : la croissance s'arrête.</p>
      <p v-if="laborShort" class="tt-warn">
        Main-d'œuvre insuffisante : production réduite à {{ laborRatioPct }}%.
      </p>
    </template>

    <!-- Devise (flouz) : solde global + revenu. -->
    <ul v-else-if="isDevise" class="tt-rows">
      <li class="tt-row">
        <span class="tt-label">Solde</span>
        <span class="tt-value">{{ formatNumber(resource.available) }}</span>
      </li>
      <li class="tt-row">
        <span class="tt-label">Revenu</span>
        <span class="tt-value" :class="productionPositive ? 'is-pos' : 'is-neg'">
          {{ productionPositive ? '+' : '' }}{{ formatRate(resource.productionPerHour) }}/h
        </span>
      </li>
    </ul>

    <!-- Consommable (défaut) : disponible / production / stockage. -->
    <template v-else>
      <ul class="tt-rows">
        <li class="tt-row">
          <span class="tt-label">Disponible</span>
          <span class="tt-value">{{ formatNumber(resource.available) }}</span>
        </li>
        <li class="tt-row">
          <span class="tt-label">Production</span>
          <span class="tt-value" :class="productionPositive ? 'is-pos' : 'is-neg'">
            {{ productionPositive ? '+' : '' }}{{ formatRate(resource.productionPerHour) }}/h
          </span>
        </li>
        <li class="tt-row">
          <span class="tt-label">Stockage</span>
          <span v-if="isUnlimited" class="tt-value text-muted">illimite</span>
          <span v-else class="tt-value" :class="{ 'is-sat': isSaturated }">
            {{ formatNumber(resource.available) }} / {{ formatNumber(resource.stored) }}
            <span class="tt-pct">({{ fillPercent }}%)</span>
          </span>
        </li>
      </ul>
      <div v-if="!isUnlimited" class="tt-bar" :class="{ 'is-sat': isSaturated }">
        <div class="tt-bar-fill" :style="{ width: `${fillPercent}%` }" />
      </div>
      <p v-if="isSaturated" class="tt-warn">Stockage sature : production en attente.</p>
    </template>
  </div>
</template>
